// Session-wide per-player action state (design stub) — the SINGLE store behind
// every surface that saves/invites/blocks a player (profile sheet, Contactos
// inline invite, post-match panel). Two surfaces disagreeing about the same
// player was an audited defect (2026-07-04): one fact, one store.
import { findPlayer, mockContacts } from '@/shared/mock/data'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'

export type PlayerActions = {
  saved?: boolean
  /** Match ids this player has been invited to (invites are PER MATCH). */
  invitedTo?: string[]
  blocked?: boolean
  noShowsDelta?: number
}

/** Record a per-match invite. */
export function invitePlayerToMatch(name: string, matchId: string): void {
  const cur = store.get(name)?.invitedTo ?? []
  if (!cur.includes(matchId)) setPlayerActions(name, { invitedTo: [...cur, matchId] })
}

export function isInvitedTo(name: string, matchId: string): boolean {
  return (store.get(name)?.invitedTo ?? []).includes(matchId)
}

const store = new Map<string, PlayerActions>()
const listeners = new Set<() => void>()

// Players already in the contacts list ARE saved — the profile sheet must
// agree with the Contactos screen from the first render (audited 2026-07-04).
//
// ⚠️ MOCK TRACK ONLY, and this line is the root of a defect FAMILY.
//
// It used to run unconditionally at module import, which meant every consumer
// of this store started life holding four fabricated people (Carlos Medina,
// Ana Pérez, Jorge López, Lucía Ramos) under real auth. `savedContactsList()`
// then handed them to Crear as invitable contacts — and because no
// `match_invites` doc was ever written, a real player's private match became
// permanently unjoinable the week `invitedIfPrivate()` started enforcing
// invites server-side (fixed 2026-08-08).
//
// Guarding at the SEED is what retires the class: every present and future
// reader of this store is now empty under real auth by construction, rather
// than each call site having to remember its own `AUTH_BYPASS` check. Three
// of the four ungated readers found in the audit were exactly that omission.
if (AUTH_BYPASS) for (const c of mockContacts) store.set(c.name, { saved: true })

export function getPlayerActions(name: string): PlayerActions {
  return store.get(name) ?? {}
}

export function setPlayerActions(name: string, patch: PlayerActions): void {
  store.set(name, { ...store.get(name), ...patch })
  listeners.forEach((l) => l())
}

/** Fresh-account reset — a brand-new identity has no saved/blocked/invited
 *  history; the seeded contacts belong to the default returning-player demo. */
export function resetPlayerActions(): void {
  store.clear()
  listeners.forEach((l) => l())
}

/** The current identity's contact list — saved && !blocked, ONE derivation
 *  for every surface that lists contacts (Contactos, Crear/Editar invites). */
export function savedContactsList(): { id: string; name: string; level: number; city: string }[] {
  return allPlayerActions()
    .filter(([, a]) => a.saved && !a.blocked)
    .map(([name]) => {
      const base = mockContacts.find((c) => c.name === name)
      if (base) return base
      const pl = findPlayer(name)
      return { id: name, name, level: pl?.level ?? 3.0, city: pl?.city ?? 'Granada' }
    })
}

/** Snapshot of every player with recorded actions (for derived lists). */
export function allPlayerActions(): [string, PlayerActions][] {
  return [...store.entries()]
}

/** Subscribe to any change; returns the unsubscribe. */
export function onPlayerActionsChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
